import * as React from 'react';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import {
  addHighlightToPage,
  clearAllHighlights,
  computeFitScale,
  pickBestMatch,
  scaleRect,
  scrollToHighlight,
  type HighlightMatch,
} from '../../utils/pdfClauseHighlight';
import apiService from '../../services/api';

type PdfJsLib = typeof import('pdfjs-dist/legacy/build/pdf.mjs');

let pdfjsLibPromise: Promise<PdfJsLib> | null = null;

async function loadPdfJsLib(): Promise<PdfJsLib> {
  if (!pdfjsLibPromise) {
  // Legacy build embeds Promise.withResolvers (and other) polyfills — required for Office WebView
  // where the separate pdf.worker thread does not see app-level polyfills.
  // Dynamic import keeps pdfjs out of the main bundle startup path (Office WebView crashes otherwise).
    pdfjsLibPromise = import('pdfjs-dist/legacy/build/pdf.mjs').then((pdfjsLib) => {
      pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('./pdf.worker.min.mjs', window.location.href).toString();
      return pdfjsLib;
    });
  }
  return pdfjsLibPromise;
}

export type PastNegotiationHighlightStatus = 'idle' | 'searching' | 'found' | 'not_found' | 'error';

export interface PastNegotiationSourcePdfPanelProps {
  pdfData: ArrayBuffer;
  /** 1-based page where the highlight was matched (from backend headers). */
  initialPage: number;
  /** Where on that page the highlight starts, as a fraction of its height (from backend headers). */
  initialTop?: number;
  totalPagesHint: number;
  /**
   * When provided, the panel will run client-side fuzzy matching via
   * `/pdf-previewer/highlight-clauses` after the PDF loads, draw a yellow
   * overlay on the best match, and scroll to it within the page (rather than
   * just to the matched page top). Used by the Sales Agreement "Go to" flow.
   * Same payload as dashboard card-header click: full clause text.
   */
  searchText?: string;
  /** Dotted clause number (e.g. 3.3.6) — same field the dashboard sends on header click. */
  clauseNumber?: string;
  /** Optional callback so the parent can show inline status (e.g. "No match"). */
  onHighlightStatus?: (status: PastNegotiationHighlightStatus) => void;
}

/**
 * Continuous vertical scroll PDF preview (all pages stacked). Lazy-renders pages near the viewport.
 */
export const PastNegotiationSourcePdfPanel: React.FC<PastNegotiationSourcePdfPanelProps> = ({
  pdfData,
  initialPage,
  initialTop,
  totalPagesHint,
  searchText,
  clauseNumber,
  onHighlightStatus,
}) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const pageRefs = React.useRef<Map<number, HTMLDivElement>>(new Map());
  const renderedPages = React.useRef<Set<number>>(new Set());
  const renderingPages = React.useRef<Set<number>>(new Set());

  const [pdfDoc, setPdfDoc] = React.useState<PDFDocumentProxy | null>(null);
  const [numPages, setNumPages] = React.useState(totalPagesHint);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageHeights, setPageHeights] = React.useState<Record<number, number>>({});
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [renderError, setRenderError] = React.useState<string | null>(null);
  const initialScrollDoneRef = React.useRef(false);
  const lastContainerWidthRef = React.useRef<number | null>(null);
  const resizeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const resizeGenerationRef = React.useRef(0);
  const [highlightStatus, setHighlightStatus] =
    React.useState<PastNegotiationHighlightStatus>('idle');
  const [highlightMatchPage, setHighlightMatchPage] = React.useState<number | null>(null);
  const highlightAppliedRef = React.useRef(false);
  const fuzzyMatchRef = React.useRef<HighlightMatch | null>(null);

  const reportStatus = React.useCallback(
    (status: PastNegotiationHighlightStatus) => {
      setHighlightStatus(status);
      onHighlightStatus?.(status);
    },
    [onHighlightStatus],
  );

  const scrollToPageInContainer = React.useCallback((
    pageNum: number,
    behavior: ScrollBehavior = 'auto',
    fractionOfPage = 0,
  ) => {
    const root = scrollRef.current;
    const el = pageRefs.current.get(pageNum);
    if (!root || !el) return;
    // To the highlight itself when its place on the page is known, with a little context above it.
    const within = fractionOfPage > 0 ? fractionOfPage * el.getBoundingClientRect().height - 48 : 0;
    const top = el.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop - 8 + Math.max(0, within);
    if (behavior === 'smooth') {
      root.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    } else {
      root.scrollTop = Math.max(0, top);
    }
  }, []);

  const setPageRef = React.useCallback((pageNum: number, el: HTMLDivElement | null) => {
    if (el) pageRefs.current.set(pageNum, el);
    else pageRefs.current.delete(pageNum);
  }, []);

  const computeScale = React.useCallback((pdfPage: PDFPageProxy, wrapWidth: number) => {
    const base = pdfPage.getViewport({ scale: 1 });
    const maxW = Math.max(200, wrapWidth - 16);
    return Math.min(maxW / base.width, 2.5);
  }, []);

  const renderPage = React.useCallback(
    async (pageNum: number) => {
      if (!pdfDoc || renderedPages.current.has(pageNum) || renderingPages.current.has(pageNum)) {
        return;
      }
      const slot = pageRefs.current.get(pageNum);
      const canvas = slot?.querySelector('canvas');
      const wrap = scrollRef.current;
      if (!slot || !canvas || !wrap) return;

      renderingPages.current.add(pageNum);
      try {
        const pdfPage = await pdfDoc.getPage(pageNum);
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const scale = computeScale(pdfPage, wrap.clientWidth);
        const viewport = pdfPage.getViewport({ scale });
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await pdfPage.render({ canvasContext: ctx, viewport }).promise;
        renderedPages.current.add(pageNum);
      } catch (e: unknown) {
        setRenderError(e instanceof Error ? e.message : 'Failed to render page');
      } finally {
        renderingPages.current.delete(pageNum);
      }
    },
    [pdfDoc, computeScale],
  );

  const recomputePageHeights = React.useCallback(
    async (doc: PDFDocumentProxy, wrapWidth: number): Promise<Record<number, number>> => {
      const heights: Record<number, number> = {};
      for (let p = 1; p <= doc.numPages; p += 1) {
        const pdfPage = await doc.getPage(p);
        const scale = computeScale(pdfPage, wrapWidth);
        heights[p] = pdfPage.getViewport({ scale }).height;
      }
      return heights;
    },
    [computeScale],
  );

  const renderVisiblePages = React.useCallback(() => {
    const root = scrollRef.current;
    if (!root || numPages < 1) return;

    const rootRect = root.getBoundingClientRect();
    const margin = 400;
    const visibleTop = rootRect.top - margin;
    const visibleBottom = rootRect.bottom + margin;
    const toRender = new Set<number>();

    for (let p = 1; p <= numPages; p += 1) {
      const el = pageRefs.current.get(p);
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (r.bottom >= visibleTop && r.top <= visibleBottom) {
        toRender.add(p);
        if (p > 1) toRender.add(p - 1);
        if (p < numPages) toRender.add(p + 1);
      }
    }

    toRender.forEach((p) => void renderPage(p));
  }, [numPages, renderPage]);

  React.useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    setPdfDoc(null);
    setPageHeights({});
    initialScrollDoneRef.current = false;
    highlightAppliedRef.current = false;
    fuzzyMatchRef.current = null;
    setHighlightMatchPage(null);
    renderedPages.current.clear();
    renderingPages.current.clear();

    void (async () => {
      try {
        const pdfjsLib = await loadPdfJsLib();
        const task = pdfjsLib.getDocument({ data: new Uint8Array(pdfData.slice(0)) });
        const pdf = await task.promise;
        if (cancelled) {
          await pdf.destroy().catch(() => {});
          return;
        }
        const matchPage = Math.min(Math.max(1, initialPage), pdf.numPages);
        setNumPages(pdf.numPages);
        setCurrentPage(matchPage);
        setPdfDoc(pdf);
      } catch (e: unknown) {
        if (!cancelled) {
          setLoadError(e instanceof Error ? e.message : 'Failed to open PDF');
        }
      }
    })();

    return () => {
      cancelled = true;
      setPdfDoc((prev) => {
        if (prev) void prev.destroy().catch(() => {});
        return null;
      });
    };
  }, [pdfData, initialPage]);

  React.useEffect(() => {
    if (!pdfDoc) return;
    let cancelled = false;
    const wrap = scrollRef.current;
    const width = wrap?.clientWidth ?? 640;

    void (async () => {
      const heights = await recomputePageHeights(pdfDoc, width);
      if (!cancelled) setPageHeights(heights);
    })();

    return () => {
      cancelled = true;
    };
  }, [pdfDoc, recomputePageHeights]);

  React.useEffect(() => {
    const container = scrollRef.current;
    if (!container || !pdfDoc) return;

    const handleResize = () => {
      const root = scrollRef.current;
      if (!root || !pdfDoc) return;

      const width = root.clientWidth;
      if (lastContainerWidthRef.current === width) return;
      lastContainerWidthRef.current = width;

      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = setTimeout(() => {
        resizeTimeoutRef.current = null;
        const scrollRoot = scrollRef.current;
        if (!scrollRoot || !pdfDoc) return;

        const scrollRatio =
          scrollRoot.scrollHeight > scrollRoot.clientHeight
            ? scrollRoot.scrollTop / (scrollRoot.scrollHeight - scrollRoot.clientHeight)
            : 0;

        renderedPages.current.clear();
        const gen = ++resizeGenerationRef.current;

        void (async () => {
          const heights = await recomputePageHeights(pdfDoc, width);
          if (gen !== resizeGenerationRef.current) return;
          setPageHeights(heights);

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (gen !== resizeGenerationRef.current) return;
              // After Go to, keep the matched highlight aligned the same way
              // the clause card header is re-aligned on expand/collapse — do
              // not restore a document-wide scroll ratio (that drifts the box).
              if (!fuzzyMatchRef.current && scrollRoot.scrollHeight > scrollRoot.clientHeight) {
                scrollRoot.scrollTop = scrollRatio * (scrollRoot.scrollHeight - scrollRoot.clientHeight);
              }
              renderVisiblePages();
            });
          });
        })();
      }, 100);
    };

    lastContainerWidthRef.current = container.clientWidth;
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(handleResize);
      ro.observe(container);
    }
    window.addEventListener('resize', handleResize);

    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
    };
  }, [pdfDoc, recomputePageHeights, renderVisiblePages]);

  React.useEffect(() => {
    if (!pdfDoc || numPages < 1) return;
    const root = scrollRef.current;
    if (!root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const pageNum = Number((entry.target as HTMLElement).dataset.page);
          if (pageNum >= 1) {
            void renderPage(pageNum);
            if (pageNum > 1) void renderPage(pageNum - 1);
            if (pageNum < numPages) void renderPage(pageNum + 1);
          }
        }
      },
      { root, rootMargin: '400px 0px', threshold: 0.01 },
    );

    const observeAll = () => {
      for (let p = 1; p <= numPages; p += 1) {
        const el = pageRefs.current.get(p);
        if (el) observer.observe(el);
      }
    };

    observeAll();
    const retryId = window.setTimeout(observeAll, 0);

    return () => {
      window.clearTimeout(retryId);
      observer.disconnect();
    };
  }, [pdfDoc, numPages, renderPage, pageHeights]);

  React.useEffect(() => {
    if (!pdfDoc || initialScrollDoneRef.current) return;
    // When searchText is provided, the fuzzy-highlight effect below owns the
    // initial scroll (to the matched rect within a page). Skipping the simple
    // page-top scroll here avoids racing the smooth scroll.
    if (searchText && searchText.trim()) return;
    const matchPage = Math.min(Math.max(1, initialPage), pdfDoc.numPages);
    if (Object.keys(pageHeights).length < matchPage) return;

    const t = window.setTimeout(() => {
      scrollToPageInContainer(matchPage, 'auto', initialTop ?? 0);
      void renderPage(matchPage);
      if (matchPage > 1) void renderPage(matchPage - 1);
      if (matchPage < pdfDoc.numPages) void renderPage(matchPage + 1);
      initialScrollDoneRef.current = true;
    }, 50);
    return () => window.clearTimeout(t);
  }, [pdfDoc, initialPage, initialTop, pageHeights, renderPage, scrollToPageInContainer, searchText]);

  // Same matcher as dashboard card-header click: POST full text + clause_number
  // to /pdf-previewer/highlight-clauses as soon as the PDF is open (do not wait
  // for every page height — that was the old delayed path).
  React.useEffect(() => {
    if (!pdfDoc) return;
    const text = (searchText ?? '').trim();
    if (!text) return;
    if (highlightAppliedRef.current) return;

    let cancelled = false;
    highlightAppliedRef.current = true;
    reportStatus('searching');

    void (async () => {
      try {
        const blob = new Blob([pdfData.slice(0)], { type: 'application/pdf' });
        const response = await apiService.highlightClausesInPdf(blob, [
          {
            id: 'sa-go-to',
            text,
            ...(clauseNumber ? { clause_number: clauseNumber } : {}),
          },
        ]);
        if (cancelled) return;

        const matches = (response?.results?.['sa-go-to'] ?? []) as HighlightMatch[];
        const best = pickBestMatch(matches);
        if (!best) {
          reportStatus('not_found');
          const fallback = Math.min(Math.max(1, initialPage), pdfDoc.numPages);
          scrollToPageInContainer(fallback, 'auto');
          initialScrollDoneRef.current = true;
          return;
        }

        fuzzyMatchRef.current = best;
        const matchPage1 = Math.min(Math.max(1, best.page + 1), pdfDoc.numPages);
        setHighlightMatchPage(matchPage1);
        reportStatus('found');
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('Sales Agreement fuzzy highlight failed:', err);
        reportStatus('error');
        const fallback = Math.min(Math.max(1, initialPage), pdfDoc.numPages);
        scrollToPageInContainer(fallback, 'auto');
        initialScrollDoneRef.current = true;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    pdfDoc,
    pdfData,
    searchText,
    clauseNumber,
    initialPage,
    scrollToPageInContainer,
    reportStatus,
  ]);

  // Once the matcher returns AND page wrappers have heights, overlay + scroll
  // to the matched rect (same scroll helper as dashboard PdfViewer).
  React.useEffect(() => {
    const best = fuzzyMatchRef.current;
    if (!pdfDoc || !best) return;
    if (!pageHeights[best.page + 1]) return;
    if (initialScrollDoneRef.current) return;

    let cancelled = false;
    const matchPage1 = Math.min(Math.max(1, best.page + 1), pdfDoc.numPages);

    void (async () => {
      await renderPage(matchPage1);
      if (matchPage1 > 1) void renderPage(matchPage1 - 1);
      if (matchPage1 < pdfDoc.numPages) void renderPage(matchPage1 + 1);
      if (cancelled) return;

      const root = scrollRef.current;
      const pageEl = pageRefs.current.get(matchPage1);
      if (!root || !pageEl) return;

      const pdfPage = await pdfDoc.getPage(matchPage1);
      if (cancelled) return;
      const baseViewport = pdfPage.getViewport({ scale: 1 });
      const scale = computeFitScale(baseViewport.width, root.clientWidth);
      const scaledRect = scaleRect(best.rect, scale);

      requestAnimationFrame(() => {
        if (cancelled) return;
        const layerHost = pageRefs.current.get(matchPage1);
        if (!layerHost) return;
        clearAllHighlights(root);
        addHighlightToPage(layerHost, scaledRect);
        scrollToHighlight(root, layerHost, scaledRect, { topPadding: 80 });
        initialScrollDoneRef.current = true;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [pdfDoc, pageHeights, highlightMatchPage, renderPage]);

  // After expand/collapse (pageHeights change), redraw the highlight at the
  // new fit-scale AND re-scroll to it — same settle pattern as the clause card
  // (`scheduleScrollClauseHeaderIntoView`: run + double rAF + 160ms).
  React.useEffect(() => {
    const root = scrollRef.current;
    const match = fuzzyMatchRef.current;
    if (!root || !pdfDoc || !match) return;
    if (!initialScrollDoneRef.current) return;
    if (Object.keys(pageHeights).length < pdfDoc.numPages) return;

    let cancelled = false;
    const align = async () => {
      if (cancelled) return;
      const matchPage1 = Math.min(Math.max(1, match.page + 1), pdfDoc.numPages);
      await renderPage(matchPage1);
      if (cancelled) return;
      const pageEl = pageRefs.current.get(matchPage1);
      if (!pageEl) return;
      const pdfPage = await pdfDoc.getPage(matchPage1);
      if (cancelled) return;
      const baseViewport = pdfPage.getViewport({ scale: 1 });
      const scale = computeFitScale(baseViewport.width, root.clientWidth);
      const scaledRect = scaleRect(match.rect, scale);
      clearAllHighlights(root);
      addHighlightToPage(pageEl, scaledRect);
      scrollToHighlight(root, pageEl, scaledRect, { topPadding: 80, behavior: 'auto' });
    };

    const run = () => {
      void align();
    };
    run();
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
    const settleTimer = window.setTimeout(run, 160);

    return () => {
      cancelled = true;
      window.clearTimeout(settleTimer);
    };
  }, [pdfDoc, pageHeights, renderPage]);

  React.useEffect(() => {
    const root = scrollRef.current;
    if (!root || numPages < 1) return;

    const updateCurrentFromScroll = () => {
      const rootRect = root.getBoundingClientRect();
      const mid = rootRect.top + rootRect.height * 0.35;
      let best = 1;
      let bestDist = Infinity;
      for (let p = 1; p <= numPages; p += 1) {
        const el = pageRefs.current.get(p);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        const dist = Math.abs(r.top - mid);
        if (dist < bestDist) {
          bestDist = dist;
          best = p;
        }
      }
      setCurrentPage(best);
    };

    root.addEventListener('scroll', updateCurrentFromScroll, { passive: true });
    updateCurrentFromScroll();
    return () => root.removeEventListener('scroll', updateCurrentFromScroll);
  }, [numPages, pdfDoc]);

  const scrollToPage = React.useCallback(
    (pageNum: number) => {
      scrollToPageInContainer(pageNum, 'smooth');
      setCurrentPage(pageNum);
      void renderPage(pageNum);
      if (pageNum > 1) void renderPage(pageNum - 1);
      if (pageNum < numPages) void renderPage(pageNum + 1);
    },
    [renderPage, numPages, scrollToPageInContainer],
  );

  if (loadError) {
    return <p className="cc-source-pdf-error">{loadError}</p>;
  }

  if (!pdfDoc) {
    return (
      <div className="cc-source-loading" role="status" aria-live="polite">
        <span className="cc-source-loading-spinner" aria-hidden />
        <p className="cc-source-loading-text">Loading the source…</p>
      </div>
    );
  }

  const fuzzyEnabled = !!(searchText && searchText.trim());
  // Prefer the fuzzy match page once we have one; otherwise fall back to the
  // server-provided page hint (used by the past-negotiation Source modal).
  const matchPage = fuzzyEnabled
    ? highlightMatchPage ?? null
    : Math.min(Math.max(1, initialPage), numPages);
  const showMatchBadge = matchPage != null && matchPage === currentPage;
  const fuzzyStatusText: string | null = fuzzyEnabled
    ? highlightStatus === 'searching'
      ? 'Searching…'
      : highlightStatus === 'not_found'
        ? 'No match in PDF'
        : highlightStatus === 'error'
          ? 'Search failed'
          : null
    : null;

  return (
    <div className="cc-source-pdf-panel">
      <div className="cc-source-pdf-toolbar">
        <button
          type="button"
          className="cc-source-pdf-nav"
          disabled={currentPage <= 1}
          onClick={() => scrollToPage(Math.max(1, currentPage - 1))}
        >
          Previous
        </button>
        <span className="cc-source-pdf-page-label">
          Page {currentPage} of {numPages}
          {showMatchBadge && <span className="cc-source-pdf-badge"> Match</span>}
        </span>
        {fuzzyStatusText && (
          <span
            className={`cc-source-pdf-status cc-source-pdf-status--${highlightStatus}`}
            role="status"
            aria-live="polite"
          >
            {fuzzyStatusText}
          </span>
        )}
        <button
          type="button"
          className="cc-source-pdf-nav"
          disabled={currentPage >= numPages}
          onClick={() => scrollToPage(Math.min(numPages, currentPage + 1))}
        >
          Next
        </button>
      </div>
      {renderError && <p className="cc-source-pdf-error">{renderError}</p>}
      <div ref={scrollRef} className="cc-source-pdf-canvas-wrap cc-source-pdf-canvas-wrap--continuous">
        <div className="cc-source-pdf-pages">
          {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => (
            <div
              key={pageNum}
              ref={(el) => setPageRef(pageNum, el)}
              className="cc-source-pdf-page"
              data-page={pageNum}
              style={pageHeights[pageNum] ? { minHeight: pageHeights[pageNum] } : undefined}
            >
              <canvas className="cc-source-pdf-canvas" aria-label={`Page ${pageNum}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
